"use client"

import { useMemo, useState, type ReactNode } from "react"
import {
  SectionRule,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { readCount, readNonNegative, readQuantity } from '@/lib/tools/numeric-input'

// ---------- Calc logic ----------
// Standard amortization formula:
//   M = P × r(1+r)^n / ((1+r)^n − 1)
// where r = APR / 12, n = term in months.
// At r = 0, M = P / n (simple division — no interest).
//
// Sources:
// - National retail-credit APR baseline: FRED commercial-bank credit-card
//   APR series (recent reads ~21–22% mean; store-card-specific surveys
//   typically run higher). We default the personal loan input to 11.5%
//   reflecting a stronger-credit consumer; the user can edit.
// - "0% deferred-interest" terms reflect the standard structure published
//   by Synchrony (CareCredit, store-branded consumer cards) and Wells
//   Fargo retail-services accounts: interest accrues from day 1 and is
//   applied retroactively if the balance is not paid in full by the end
//   of the promotional period.
function calcMonthlyPayment(principal: number, annualRate: number, months: number): number {
  if (annualRate === 0) return principal / months
  const r = annualRate / 100 / 12
  return (principal * (r * Math.pow(1 + r, months))) / (Math.pow(1 + r, months) - 1)
}

interface OptionResult {
  key: "personal" | "bnpl" | "promo"
  name: string
  monthly: number
  totalIfPaid: number
  totalIfNotPaid: number | null
  interestIfPaid: number
}

interface Results {
  options: OptionResult[]
  cheapest: "personal" | "bnpl" | "promo"
  worstCase: "personal" | "bnpl" | "promo"
  promoIfNotPaidExtra: number
}

/**
 * Every parameter is `number | null` — what the readers in
 * lib/tools/numeric-input.ts return — and the APR guards test `=== null`
 * accordingly (FRIDGE-411).
 *
 * They were typed `number | undefined` and guarded with `=== undefined`, which
 * this project's typechecker cannot catch: tsconfig sets `strict: false`, so
 * `strictNullChecks` is off and `null` is assignable to `number | undefined`
 * with no complaint. The guard therefore never fired for a null, and a null APR
 * reached `calcMonthlyPayment`, where it coerces to 0 and produces a confident
 * 0%-interest schedule.
 *
 * That was already reachable before the conversion — the old clamping reader
 * returned null for an EMPTY field — so an empty APR box already rendered a
 * full 0% comparison. The conversion widens it to any rejected value, which is
 * what surfaced it.
 *
 * An APR of exactly 0 is a real promotional rate, so these three cannot use a
 * falsy check: `!plApr` would throw away the answer a visitor most wants.
 */
function computeResults(
  price: number | null,
  plApr: number | null,
  plTerm: number | null,
  bApr: number | null,
  bTerm: number | null,
  promoMonths: number | null,
  deferredApr: number | null,
): Results | null {
  if (!price || plApr === null || !plTerm || bApr === null || !bTerm || !promoMonths || deferredApr === null) {
    return null
  }

  const plMonthly = calcMonthlyPayment(price, plApr, plTerm)
  const plTotal = plMonthly * plTerm
  const plInterest = plTotal - price

  const bMonthly = calcMonthlyPayment(price, bApr, bTerm)
  const bTotal = bMonthly * bTerm
  const bInterest = bTotal - price

  const promoMonthly = price / promoMonths
  const promoTotalIfPaid = price
  // Deferred-interest worst case: if not paid in full by end of promo,
  // accrued interest from day 1 is added back. Standard Synchrony/Wells
  // Fargo terms — we approximate as APR × principal × (promo months / 12).
  const promoTotalIfNotPaid = price + (price * deferredApr / 100) * (promoMonths / 12)

  const options: OptionResult[] = [
    {
      key: "personal",
      name: "Personal loan",
      monthly: plMonthly,
      totalIfPaid: plTotal,
      totalIfNotPaid: null,
      interestIfPaid: plInterest,
    },
    {
      key: "bnpl",
      name: "Buy now, pay later",
      monthly: bMonthly,
      totalIfPaid: bTotal,
      totalIfNotPaid: null,
      interestIfPaid: bInterest,
    },
    {
      key: "promo",
      name: "Retailer 0% promo",
      monthly: promoMonthly,
      totalIfPaid: promoTotalIfPaid,
      totalIfNotPaid: promoTotalIfNotPaid,
      interestIfPaid: 0,
    },
  ]

  const sortedByPaid = [...options].sort((a, b) => a.totalIfPaid - b.totalIfPaid)
  const cheapest = sortedByPaid[0].key

  const worstSorted = [...options].sort((a, b) => {
    const av = a.totalIfNotPaid ?? a.totalIfPaid
    const bv = b.totalIfNotPaid ?? b.totalIfPaid
    return bv - av
  })
  const worstCase = worstSorted[0].key

  return {
    options,
    cheapest,
    worstCase,
    promoIfNotPaidExtra: promoTotalIfNotPaid - price,
  }
}

// ---------- Sides ----------

interface Side {
  key: "fixed" | "promo"
  name: string
  tag: string
  icon: string
  color: string
  pros: string[]
  cons: string[]
}

const SIDE_FIXED: Side = {
  key: "fixed",
  name: "Fixed-APR loan",
  tag: "The predictable pick",
  icon: "%",
  color: "#0469f0",
  pros: [
    "Same payment every month",
    "Total cost is locked from day 1",
    "No deferred-interest trap",
    "Builds payment history if reported",
  ],
  cons: [
    "You pay interest on every dollar",
    "APR depends on credit score",
    "Origination fees on some lenders",
  ],
}

const SIDE_PROMO: Side = {
  key: "promo",
  name: "0% deferred-interest promo",
  tag: "The high-stakes pick",
  icon: "0%",
  color: "#ff9951",
  pros: [
    "Truly $0 interest if paid in full on time",
    "Lowest possible total cost when used right",
    "No payment penalty during promo",
  ],
  cons: [
    "Interest accrues from day 1 (Synchrony/Wells Fargo terms)",
    "Miss the deadline → full retroactive interest applied",
    "Post-promo APR commonly 27%+",
    "Late payment can void the 0% offer",
  ],
}

interface Scenario {
  who: string
  winner: "fixed" | "promo"
  reason: string
}

const SCENARIOS: Scenario[] = [
  {
    who: "You auto-pay every bill on the day it arrives",
    winner: "promo",
    reason: "If you reliably clear the balance before the promo ends, deferred-interest = $0 in interest. Lowest total cost.",
  },
  {
    who: "You sometimes miss a credit-card payment",
    winner: "fixed",
    reason: "One missed promo deadline triggers retroactive interest from day 1. A fixed-APR loan caps your downside.",
  },
  {
    who: "Strong credit (720+), shopping APR",
    winner: "fixed",
    reason: "Personal-loan APRs in the 8–12% range beat the worst-case retroactive interest on a deferred-interest plan.",
  },
  {
    who: "Limited credit history",
    winner: "promo",
    reason: "Retail promos approve broader credit profiles than bank personal loans — and 0% beats any APR you'd get otherwise, IF you pay on time.",
  },
  {
    who: "Refrigerator under $1,500, 12-mo budget plan",
    winner: "promo",
    reason: "Short payoff window plus low principal makes the 0% promo a clean win with manageable monthly payments.",
  },
  {
    who: "Refrigerator over $3,500, 36-mo+ payoff",
    winner: "fixed",
    reason: "Most retailer promos top out at 18–24 months. A fixed-APR loan stretches the payment without the deferred-interest cliff.",
  },
]

interface Field {
  label: string
  hint?: string
  el: ReactNode
}

function InputField({ label, hint, el }: Field) {
  return (
    <div>
      <div className="mb-2">
        <label className="block text-[12px] font-bold uppercase tracking-[0.1em] text-gray-900">
          {label}
        </label>
        {hint && <span className="block text-[10px] text-gray-500 mt-0.5">{hint}</span>}
      </div>
      {el}
    </div>
  )
}

// ---------- Page ----------

export function FinancingComparisonToolClient(_props?: { toolData?: unknown }) {
  const [price, setPrice] = useState("2000")
  const [plApr, setPlApr] = useState("11.5")
  const [plTerm, setPlTerm] = useState("36")
  const [bApr, setBApr] = useState("0")
  const [bTerm, setBTerm] = useState("24")
  const [promoMonths, setPromoMonths] = useState("12")
  const [deferredApr, setDeferredApr] = useState("27.99")
  const [tab, setTab] = useState<"fixed" | "promo">("fixed")

  const priceNum = readQuantity(price, { min: 100, max: 100000 })
  const plAprNum = readNonNegative(plApr, { max: 36 })
  const plTermNum = readCount(plTerm, { min: 6, max: 84 })
  const bAprNum = readNonNegative(bApr, { max: 36 })
  const bTermNum = readCount(bTerm, { min: 3, max: 60 })
  const promoMonthsNum = readCount(promoMonths, { min: 6, max: 36 })
  const deferredAprNum = readNonNegative(deferredApr, { max: 36 })

  const results = useMemo(
    () =>
      computeResults(
        priceNum,
        plAprNum,
        plTermNum,
        bAprNum,
        bTermNum,
        promoMonthsNum,
        deferredAprNum,
      ),
    [priceNum, plAprNum, plTermNum, bAprNum, bTermNum, promoMonthsNum, deferredAprNum],
  )

  return (
    <ToolPageShell
      toolName="Financing Comparison Tool"
      heroEyebrow="Financial"
      heroTitlePre="Personal loan"
      heroTitleItalic="vs"
      heroTitlePost="0% retailer promo."
      heroSubtitle="The 0% retailer promo is cheapest if you pay it off in time — and the most expensive if you don't. Run your numbers and see which side wins for the way you actually pay bills."
      heroMeta={["⏱ ~2 min read", "FRED APR data + Synchrony/Wells Fargo terms"]}
      heroShare={<ToolShareBar title="Financing Comparison Tool" />}
      heroRight={
        <div className="rounded-xl p-6 bg-gray-50 border border-gray-200">
          <div className="flex items-start gap-3">
            <div
              className="font-serif text-[#ff9951] leading-none flex-shrink-0"
              style={{ fontSize: 40, letterSpacing: "-0.05em" }}
            >
              &ldquo;
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.15em] font-bold mb-2 text-[#ff9951]">
                The short answer
              </div>
              <p
                className="font-medium text-gray-900"
                style={{
                  fontSize: 16,
                  lineHeight: 1.5,
                  letterSpacing: "-0.01em",
                  textWrap: "pretty" as never,
                }}
              >
                Pick the <em className="italic text-[#ff9951] font-bold">0% retailer promo</em> only
                if you&apos;ll auto-pay it off before the deadline. Otherwise, pick a{" "}
                <em className="italic text-[#ff9951] font-bold">fixed-APR personal loan</em> — the
                deferred-interest trap is brutal.
              </p>
            </div>
          </div>
        </div>
      }
      faqs={[
        {
          question: "What is deferred interest, exactly?",
          answer:
            "On a 0% promo from Synchrony or Wells Fargo retail-services accounts, interest accrues from day 1 at the post-promo APR. If you pay the balance in full by the deadline, that accrued interest is waived. If even $1 remains, the full accrued amount is applied to your account retroactively.",
        },
        {
          question: "Where does the 11.5% personal-loan default come from?",
          answer:
            "We default to a mid-range APR for a strong-credit borrower. FRED commercial-bank credit-card APR data has run in the high teens to low twenties recently; personal-loan APRs typically run lower. Use your actual quote — different lenders price very differently.",
        },
        {
          question: "Why is the 0% promo cheapest in the 'best case'?",
          answer:
            "Because you literally pay $0 in interest. Total cost equals the sticker price. No financing option beats that — but only if the deadline is met.",
        },
        {
          question: "Are BNPL plans (Affirm, Klarna) safer than store deferred-interest?",
          answer:
            "Most simple-interest BNPL plans charge a stated APR upfront with no retroactive penalty. That's structurally safer than deferred interest. A 0% Affirm plan is genuinely 0% if approved — no trap. Read the disclosure on every offer.",
        },
        {
          question: "Does financing affect my credit?",
          answer:
            "Personal loans and store cards run a hard pull and report monthly payment history. Some BNPL providers do soft pulls and don't report to bureaus at all. Missing a payment on any of them can hurt your score.",
        },
      ]}
      relatedTools={[
        { title: "Energy Cost Calculator", category: "Energy & Cost", description: "Annual cost for any fridge.", href: "/tools/energy-cost-calculator", time: "~30 sec" },
        { title: "Refrigerator Repair Cost Estimator", category: "Specialty", description: "Repair vs replace math.", href: "/tools/refrigerator-repair-cost-estimator", time: "~1 min" },
        { title: "Refrigerator Lifespan Calculator", category: "Specialty", description: "Years of life remaining.", href: "/tools/refrigerator-lifespan-calculator", time: "~45 sec" },
        { title: "Find Your Fridge", category: "Quiz", description: "Five questions to a starting product group.", href: "/tools/find-your-fridge", time: "~90 sec" },
      ]}
      cta={{
        title: "Ready to shop with a budget in mind?",
        body: "Browse French door refrigerators across every major retailer with real prices.",
        buttonLabel: "Browse French door fridges",
        buttonHref: "/french-door-refrigerators",
      }}
    >
      {/* ============ COST CALCULATOR ============ */}
      <section className="py-8 md:py-12 bg-gray-50 border-b border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 grid lg:grid-cols-[460px_1fr] gap-8 lg:gap-12">
          <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 lg:sticky lg:top-24 lg:self-start">
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-5 text-[#ff9951]">
              Run your numbers
            </div>
            <div className="space-y-6">
              <InputField
                label="Refrigerator price ($)"
                hint="Sticker price before tax"
                el={
                  <input
                    type="number"
                    min={100}
                    max={100000}
                    step={50}
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold"
                  />
                }
              />

              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-gray-700">Personal loan</div>
                <div className="grid grid-cols-2 gap-3">
                  <InputField
                    label="APR (%)"
                    el={
                      <input
                        type="number"
                        min={0}
                        max={36}
                        step={0.01}
                        value={plApr}
                        onChange={(e) => setPlApr(e.target.value)}
                        className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold"
                      />
                    }
                  />
                  <InputField
                    label="Term (months)"
                    el={
                      <input
                        type="number"
                        min={6}
                        max={84}
                        value={plTerm}
                        onChange={(e) => setPlTerm(e.target.value)}
                        className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold"
                      />
                    }
                  />
                </div>
              </div>

              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-gray-700">BNPL</div>
                <div className="grid grid-cols-2 gap-3">
                  <InputField
                    label="APR (%)"
                    el={
                      <input
                        type="number"
                        min={0}
                        max={36}
                        step={0.01}
                        value={bApr}
                        onChange={(e) => setBApr(e.target.value)}
                        className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold"
                      />
                    }
                  />
                  <InputField
                    label="Term (months)"
                    el={
                      <input
                        type="number"
                        min={3}
                        max={60}
                        value={bTerm}
                        onChange={(e) => setBTerm(e.target.value)}
                        className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold"
                      />
                    }
                  />
                </div>
              </div>

              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-gray-700">Retailer 0% promo</div>
                <div className="grid grid-cols-2 gap-3">
                  <InputField
                    label="Promo months"
                    el={
                      <input
                        type="number"
                        min={6}
                        max={36}
                        value={promoMonths}
                        onChange={(e) => setPromoMonths(e.target.value)}
                        className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold"
                      />
                    }
                  />
                  <InputField
                    label="Post-promo APR (%)"
                    el={
                      <input
                        type="number"
                        min={0}
                        max={36}
                        step={0.01}
                        value={deferredApr}
                        onChange={(e) => setDeferredApr(e.target.value)}
                        className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold"
                      />
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          <div>
            {results && (
              <>
                <div className="grid sm:grid-cols-3 gap-4 mb-6">
                  {results.options.map((o) => {
                    const isCheapest = o.key === results.cheapest
                    return (
                      <div
                        key={o.key}
                        className="rounded-xl p-5 border-2"
                        style={{
                          borderColor: isCheapest ? "#0469f0" : "#e5e7eb",
                          background: isCheapest ? "rgba(4,105,240,0.04)" : "white",
                        }}
                      >
                        <div
                          className="text-[10px] font-bold uppercase tracking-[0.15em] mb-1"
                          style={{ color: isCheapest ? "#0469f0" : "#6b7280" }}
                        >
                          {isCheapest ? "Cheapest if paid on time" : "Option"}
                        </div>
                        <div className="text-[18px] font-black tracking-tight text-gray-900 mb-3">
                          {o.name}
                        </div>
                        <div className="space-y-2">
                          <div>
                            <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-gray-500">Monthly</div>
                            <div className="font-black text-gray-900" style={{ fontSize: 22, letterSpacing: "-0.02em" }}>
                              {`$${o.monthly.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            </div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-gray-500">Total if paid on time</div>
                            <div className="font-black text-gray-900" style={{ fontSize: 18, letterSpacing: "-0.02em" }}>
                              {`$${o.totalIfPaid.toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
                            </div>
                          </div>
                          {o.totalIfNotPaid !== null && (
                            <div>
                              <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-red-600">Worst case (missed deadline)</div>
                              <div className="font-black text-red-600" style={{ fontSize: 18, letterSpacing: "-0.02em" }}>
                                {`$${o.totalIfNotPaid.toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>

                <div className="grid grid-cols-2 border-y border-gray-300 divide-x divide-gray-300 mb-6">
                  <div className="py-5 pr-5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">Cheapest path</div>
                    <div className="font-black" style={{ fontSize: 24, letterSpacing: "-0.03em", color: "#0469f0" }}>
                      {results.options.find((o) => o.key === results.cheapest)?.name}
                    </div>
                  </div>
                  <div className="py-5 pl-5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">Worst-case extra cost</div>
                    <div className="font-black text-red-600" style={{ fontSize: 24, letterSpacing: "-0.03em" }}>
                      +{`$${Math.round(results.promoIfNotPaidExtra).toLocaleString()}`}
                    </div>
                  </div>
                </div>

                <p className="text-[13px] text-gray-500 leading-[1.5]">
                  Worst-case applies if a 0% deferred-interest promo isn&apos;t paid in full by the
                  deadline — accrued interest from day 1 at the post-promo APR is added back.
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ============ SPEC FIGHT ============ */}
      <section className="bg-white py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="Side by side" title="The spec fight" right="2 structures" />

          <div className="grid md:grid-cols-2 gap-5">
            {[SIDE_FIXED, SIDE_PROMO].map((s, i) => (
              <div
                key={s.name}
                className="rounded-xl p-6 md:p-7"
                style={{
                  border: `2px solid ${s.color}`,
                  background: i === 0 ? "rgba(4,105,240,0.03)" : "rgba(255,153,81,0.03)",
                }}
              >
                <div className="flex items-center justify-between mb-4 pb-4 border-b" style={{ borderColor: `${s.color}30` }}>
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em]" style={{ color: s.color }}>
                      {i === 0 ? "Side A" : "Side B"}
                    </div>
                    <div className="text-[26px] font-black tracking-tight text-gray-900">{s.name}</div>
                    <div className="text-[12px] text-gray-500 mt-0.5">{s.tag}</div>
                  </div>
                  <div className="text-3xl font-black" style={{ color: s.color }}>{s.icon}</div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-green-700 mb-2">✓ Pros</div>
                    <ul className="space-y-1.5 text-[13px] text-gray-700">
                      {s.pros.map((p) => <li key={p} className="leading-[1.4]">{p}</li>)}
                    </ul>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-500 mb-2">✕ Cons</div>
                    <ul className="space-y-1.5 text-[13px] text-gray-500">
                      {s.cons.map((c) => <li key={c} className="leading-[1.4]">{c}</li>)}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SCENARIOS ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[960px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="Which wins for you?" title="Six scenarios, six verdicts" />
          <div className="border-t border-gray-900">
            {SCENARIOS.map((s, i) => (
              <div
                key={s.who}
                className="grid grid-cols-[auto_1fr] md:grid-cols-[auto_1fr_auto] gap-3 md:gap-5 items-start md:items-center py-4 md:py-5 border-b border-gray-900"
              >
                <div className="font-mono text-[13px] font-bold min-w-[28px] text-[#ff9951] pt-1 md:pt-0">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div>
                  <h3 className="text-[15px] md:text-[16px] font-extrabold tracking-[-0.015em] text-gray-900">{s.who}</h3>
                  <p className="text-[13px] text-gray-600 mt-0.5 leading-[1.5]">{s.reason}</p>
                </div>
                <div className="col-start-2 md:col-auto md:text-right mt-2 md:mt-0">
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-bold text-white text-[12px]"
                    style={{ background: s.winner === "fixed" ? "#0469f0" : "#ff9951" }}
                  >
                    {s.winner === "fixed" ? "Fixed-APR loan" : "0% promo"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PRIMARY CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Shop the category"
          title="French door refrigerators to compare"
          description="Wide shelves, a bottom freezer and a broad price range."
          collectionType="french-door"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-white py-8 md:py-12 border-t border-gray-200">
        <div className="max-w-[960px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="How we calculated" title="Methodology" />
          <div className="prose prose-sm max-w-none text-gray-700 space-y-3">
            <p>
              <strong>Monthly payment formula.</strong> Standard amortization:{" "}
              <code className="text-[12px] bg-gray-100 px-1.5 py-0.5 rounded">
                M = P × r(1+r)^n / ((1+r)^n − 1)
              </code>{" "}
              where P is principal, r is monthly rate (APR ÷ 12), and n is term in months. At 0%
              APR, M = P / n.
            </p>
            <p>
              <strong>Personal-loan APR baseline.</strong> We default to 11.5% as a mid-range value
              for a strong-credit consumer. Authoritative APR data is published by the{" "}
              <strong>FRED</strong> commercial-bank credit-card APR series (Federal Reserve
              Economic Data). Your actual quote depends on credit score, lender, and term.
            </p>
            <p>
              <strong>Deferred-interest worst case.</strong> &quot;0% APR for X months&quot; promos from
              Synchrony (CareCredit, store-branded retail credit) and Wells Fargo retail-services
              accounts are <em>deferred-interest</em>: interest accrues from day 1 at the post-promo
              APR, and is applied retroactively to the original balance if not paid in full by the
              deadline. Worst-case figure ≈ post-promo APR × principal × (promo months ÷ 12).
            </p>
            <p>
              <strong>What&apos;s not modeled.</strong> Origination fees, late-payment penalties, credit-
              card cash-back rewards, and lender-specific terms vary widely. Read your offer
              disclosure before signing.
            </p>
          </div>
        </div>
      </section>

      {/* ============ SIDE TABS — secondary carousel ============ */}
      <section className="bg-white py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="Deep dive" title="Models to compare, by price tier" />
          <div className="flex gap-2 mb-6 flex-wrap">
            {(
              [
                ["fixed", "Mid-tier ($1,500–$3,500)"],
                ["promo", "Premium ($3,500+)"],
              ] as const
            ).map(([v, l]) => (
              <button
                key={v}
                type="button"
                onClick={() => setTab(v)}
                className={[
                  "px-5 py-2.5 rounded-full font-bold text-[12px] transition-all border border-gray-900",
                  tab === v ? "bg-gray-900 text-white" : "bg-white text-gray-900 hover:bg-gray-50",
                ].join(" ")}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <ToolProductCarousel
          key={tab}
          title={tab === "fixed" ? "Mid-tier French door picks" : "Premium side-by-side picks"}
          collectionType={tab === "fixed" ? "french-door" : "side-by-side"}
          description={
            tab === "fixed"
              ? "The sweet-spot price tier where 0% promos pay off if you can clear them in 12–18 months."
              : "Higher-ticket builds where a fixed-APR personal loan often beats a promo's payoff window."
          }
        />
      </section>
    </ToolPageShell>
  )
}
